import type { CodeableConcept } from '@medplum/fhirtypes'
import {
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { Button, type ButtonProps } from '@seen/ui'
import { type JSX, type RefObject, useMemo } from 'react'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'

export interface CodeableConceptComboboxProps extends Omit<
  ButtonProps,
  'value' | 'onChange'
> {
  options: CodeableConcept[]
  value: CodeableConcept
  onChange: (value: CodeableConcept) => void
  ref?: RefObject<HTMLButtonElement>
}

export function CodeableConceptComboboxButton({
  options,
  value,
  onChange,
  ...rest
}: CodeableConceptComboboxProps): JSX.Element {
  const comboboxOptions = useMemo(
    () =>
      options.map((option) => {
        const code = codeFromCodeableConcept(option)
        invariant(code, 'code is required')
        const display = displayFromCodeableConcept(option)
        invariant(display, 'display is required')
        return {
          value: code,
          label: (
            <ComboboxOption
              isSelected={code === codeFromCodeableConcept(value)}
            >
              {display}
            </ComboboxOption>
          ),
          keywords: [display],
        }
      }),
    [options, value]
  )
  const code = codeFromCodeableConcept(value)
  invariant(code, 'code is required')
  return (
    <Combobox
      options={comboboxOptions}
      onSelect={(v) => {
        const option = options.find(
          (option) => codeFromCodeableConcept(option) === v
        )
        invariant(option, 'option is required')
        onChange(option)
      }}
    >
      <Button variant="outline" {...rest}>
        {value && displayFromCodeableConcept(value)}
      </Button>
    </Combobox>
  )
}
